// Pure, dependency-free helpers for the OWNER-facing staff-login management UI
// (Phase 5). Mirrors the validation in supabase/functions/staff-access/lib.ts so
// the form can give instant feedback before calling the edge function. These are
// UI conveniences only — the edge function re-validates everything server-side.

export type StaffCalendarScope = "own" | "all_readonly";

/** Username policy: 3–30 chars, lowercase letters/digits/dot/underscore/hyphen. */
export const STAFF_USERNAME_RE = /^[a-z0-9._-]{3,30}$/;
export const STAFF_PASSWORD_MIN_LENGTH = 8;

/** Normalise a username exactly like the edge function (trim + lowercase). */
export function normalizeStaffUsername(raw: string): string {
  return raw.trim().toLowerCase();
}

/** Is this a valid staff username (after normalisation)? */
export function isValidStaffUsername(raw: string): boolean {
  return STAFF_USERNAME_RE.test(normalizeStaffUsername(raw));
}

/** Does this password meet the minimum policy? */
export function isValidStaffPassword(pw: string): boolean {
  return typeof pw === "string" && pw.length >= STAFF_PASSWORD_MIN_LENGTH;
}

/**
 * Read the salon-wide staff calendar scope from tenant settings.
 * Default 'own' (staff see only their own appointments). Mirrors the DB helper
 * public.staff_calendar_scope() which falls back to 'own'.
 */
export function getStaffCalendarScope(
  settings: { staff_calendar_scope?: string | null } | null | undefined,
): StaffCalendarScope {
  return settings?.staff_calendar_scope === "all_readonly" ? "all_readonly" : "own";
}

/** Map the "show whole salon calendar" switch (on = all_readonly) to the stored value. */
export function scopeFromShowAll(showAll: boolean): StaffCalendarScope {
  return showAll ? "all_readonly" : "own";
}

export type StaffCustomerScope = "own" | "all";

/**
 * Read the salon-wide staff customer scope from tenant settings.
 * Default 'own' (staff see only their own customers). Mirrors the DB helper
 * public.staff_customer_scope().
 */
export function getStaffCustomerScope(
  settings: { staff_customer_scope?: string | null } | null | undefined,
): StaffCustomerScope {
  return settings?.staff_customer_scope === "all" ? "all" : "own";
}

/**
 * Binary staff customer access: true = staff may see & edit the full customer
 * list; false = no customer list for staff (default). Stored as
 * staff_customer_scope = 'all' (full) vs anything else (none).
 */
export function staffHasFullCustomerAccess(
  settings: { staff_customer_scope?: string | null } | null | undefined,
): boolean {
  return settings?.staff_customer_scope === "all";
}
